"use client"

import {
  Check,
  Loader2,
  Clock,
  MessageSquareText,
  Search,
  Database,
  BrainCircuit,
  Presentation,
  FileDown,
  Zap,
  Send,
  Inbox,
  ArrowRight,
  Download,
} from "lucide-react"
import type { ToolCall } from "@/lib/agent-demo-data"

/** 与 globals .agent-outcome-* 对应；不跟 primary 混用 */
function outcomeClass(toolCall: ToolCall): string {
  if (toolCall.resultTone === "success") return "agent-outcome-success"
  if (toolCall.resultTone === "warning") return "agent-outcome-warn"
  if (toolCall.resultTone === "neutral") return "agent-outcome-neutral"
  const r = toolCall.result || ""
  if (/未安装|需补充|失败|错误|缺少/.test(r)) return "agent-outcome-warn"
  if (/完成|就绪|成功|已确认|匹配|已就绪/.test(r)) return "agent-outcome-success"
  return "agent-outcome-neutral"
}

const CATEGORY_ICON: Record<ToolCall["category"], React.ReactNode> = {
  feishu: <MessageSquareText className="h-3 w-3" />,
  search: <Search className="h-3 w-3" />,
  data: <Database className="h-3 w-3" />,
  ai: <BrainCircuit className="h-3 w-3" />,
  ppt: <Presentation className="h-3 w-3" />,
  file: <FileDown className="h-3 w-3" />,
  general: <Zap className="h-3 w-3" />,
}

const CATEGORY_COLOR: Record<ToolCall["category"], string> = {
  feishu: "text-primary",
  search: "text-amber-500",
  data: "text-primary",
  ai: "text-violet-500",
  ppt: "text-primary",
  file: "text-teal-500",
  general: "text-slate-500",
}

function StatusIcon({ status }: { status: ToolCall["status"] }) {
  if (status === "done") return <Check className="h-3 w-3 text-primary" />
  if (status === "running") return <Loader2 className="h-3 w-3 text-primary animate-spin" />
  if (status === "waiting") return <Clock className="h-3 w-3 text-amber-500 animate-pulse" />
  return null
}

function isInstallTool(toolCall: ToolCall): boolean {
  const t = toolCall.tool.toLowerCase()
  return (
    t.includes("install") ||
    /安装/.test(toolCall.label) ||
    /安装/.test(toolCall.paramLabel || "")
  )
}

export function ToolCallItem({ toolCall }: { toolCall: ToolCall }) {
  const icon = isInstallTool(toolCall) ? (
    <Download className="h-3 w-3" />
  ) : (
    CATEGORY_ICON[toolCall.category] || CATEGORY_ICON.general
  )
  const color = CATEGORY_COLOR[toolCall.category] || CATEGORY_COLOR.general

  /* ── Feishu: chat bubble style ── */
  if (toolCall.category === "feishu") {
    const isSend = toolCall.tool.includes("send") || toolCall.tool.includes("Send")
    const isWait = toolCall.tool.includes("wait") || toolCall.tool.includes("Wait")
    const isReceive = toolCall.tool.includes("receive") || toolCall.tool.includes("Receive")

    // Extract contact name from label (e.g. "发消息给小张" -> "小张", "等待回复" uses paramLabel "小张 输入中...")
    const extractName = () => {
      // From "wait" paramLabel: "小张 输入中..." -> "小张"
      if (isWait && toolCall.paramLabel) {
        return toolCall.paramLabel.replace(" 输入中...", "").trim()
      }
      // From "send" label: "发消息给小张" / "发送给李总"
      if (isSend && toolCall.label) {
        const m = toolCall.label.match(/给(.+)$/)
        if (m) return m[1]
      }
      // From "receive" — try to get from nearby context via label
      if (isReceive && toolCall.paramLabel) {
        return toolCall.paramLabel.trim()
      }
      return ""
    }
    const contactName = extractName() || "联系人"
    const initials = contactName.slice(0, 1)

    if (isWait) {
      if (toolCall.status === "done") {
        return (
          <div className="flex items-center gap-2 py-1.5">
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20 text-primary text-micro font-medium flex-shrink-0">
              <Check className="h-3 w-3" />
            </div>
            <span className="text-xs text-muted-foreground">{contactName}</span>
            <span className="text-micro px-1.5 py-0.5 rounded bg-primary/10 dark:bg-primary/15 text-primary font-medium">已回复</span>
          </div>
        )
      }
      return (
        <div className="flex items-center gap-2 py-1.5 text-muted-foreground">
          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 text-micro font-medium flex-shrink-0">
            {initials}
          </div>
          <span className="text-xs">{contactName}</span>
          <span className="text-micro text-muted-foreground/70 flex items-center gap-1">
            <span className="inline-block w-1 h-1 rounded-full bg-amber-400 animate-pulse" />
            <span className="inline-block w-1 h-1 rounded-full bg-amber-400 animate-pulse" style={{ animationDelay: "0.2s" }} />
            <span className="inline-block w-1 h-1 rounded-full bg-amber-400 animate-pulse" style={{ animationDelay: "0.4s" }} />
            输入中
          </span>
        </div>
      )
    }

    if (isReceive) {
      const senderName = toolCall.paramLabel || contactName
      return (
        <div className="flex items-start gap-2 py-1">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20 text-primary text-micro font-medium flex-shrink-0 mt-0.5">
            {senderName.slice(0, 1)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-micro text-muted-foreground mb-0.5">{senderName}</p>
            <p className="text-xs text-foreground/80 bg-secondary/80 rounded-lg rounded-tl-sm px-2.5 py-1.5 inline-block">
              {toolCall.result}
            </p>
          </div>
        </div>
      )
    }

    if (isSend) {
      return (
        <div className="flex justify-end py-1">
          <div className="max-w-[85%]">
            <div className="text-xs text-foreground/90 bg-primary text-primary-foreground rounded-lg rounded-tr-sm px-2.5 py-1.5">
              <p>{toolCall.paramLabel}</p>
            </div>
            {toolCall.result && toolCall.status === "done" && (
              <p className="text-micro text-muted-foreground mt-0.5 text-right flex items-center justify-end gap-1">
                <Check className="h-2.5 w-2.5 text-primary" />
                <span>{toolCall.result}</span>
              </p>
            )}
          </div>
        </div>
      )
    }
  }

  /* ── Default: compact inline row ── */
  return (
    <div className="flex items-center gap-2 py-0.5">
      <span className={color}>{icon}</span>
      <span className="text-xs text-foreground/80">{toolCall.label}</span>
      {toolCall.paramLabel && toolCall.status !== "done" && (
        <span className="text-caption text-muted-foreground truncate max-w-[40%]">{toolCall.paramLabel}</span>
      )}
      {toolCall.result && toolCall.status === "done" && (
        <>
          <ArrowRight className="h-3 w-3 text-muted-foreground/40 flex-shrink-0" />
          <span className={`text-caption truncate ${outcomeClass(toolCall)}`}>{toolCall.result}</span>
        </>
      )}
      <span className="ml-auto flex-shrink-0">
        <StatusIcon status={toolCall.status} />
      </span>
    </div>
  )
}
